import React from 'react'
import { CssBaseline, ThemeProvider, createTheme, Box, Typography } from '@mui/material'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import Navigation from '../shared/components/Navigation'
import Home from './pages/Home'
import ExplorePoems from '../features/poems/pages/ExplorePoems'
import PoemDetail from '../features/poems/pages/PoemDetail'
import Contact from './pages/Contact'
import ForgotPassword from '../features/auth/pages/ForgotPassword'
import Login from '../features/auth/pages/Login'
import OAuthCallback from '../features/auth/pages/OAuthCallback'
import LogoutConfirmation from '../features/auth/pages/LogoutConfirmation'
import NotFound from './pages/NotFound'
import Register from '../features/auth/pages/Register'
import ErrorBoundary from '../shared/components/ErrorBoundary'
import RequireUser from '../features/auth/guards/RequireUser'
import Footer from '../shared/components/Footer'
import { ToastProvider } from '../shared/components/ToastProvider'
import { AuthProvider } from '../features/auth/AuthProvider'
import '../shared/utils/debugLogger' // Initialize debug logger

const Admin = React.lazy(() => import('../features/admin/pages/Admin'))
const ManagePoems = React.lazy(() => import('../features/admin/pages/ManagePoems'))
const AdminPoemCreate = React.lazy(() => import('../features/admin/pages/AdminPoemCreate'))
const AdminPoemEdit = React.lazy(() => import('../features/admin/pages/AdminPoemEdit'))
const AdminPanel = React.lazy(() => import('../features/admin/pages/AdminPanel'))
const Favorites = React.lazy(() => import('../features/poems/pages/Favorites'))
const UserProfile = React.lazy(() => import('../features/profile/pages/UserProfile'))

const RouteLoadingFallback: React.FC = () => (
  <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
    <Typography color="text.secondary">Loading...</Typography>
  </Box>
)

const withRouteSuspense = (element: React.ReactElement) => (
  <React.Suspense fallback={<RouteLoadingFallback />}>
    {element}
  </React.Suspense>
)

const getStoredThemeMode = (): 'light' | 'dark' => {
  const stored = localStorage.getItem('kav_theme')
  return stored === 'light' || stored === 'dark' ? stored : 'dark'
}

const App: React.FC = () => {
  const [mode, setMode] = React.useState<'light' | 'dark'>(getStoredThemeMode)
  React.useEffect(() => { localStorage.setItem('kav_theme', mode) }, [mode])

  const toggleTheme = React.useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'))
  }, [])

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#f59e0b' : '#d97706', // Gold/Amber
        light: mode === 'dark' ? '#fcd34d' : '#f59e0b',
        dark: mode === 'dark' ? '#d97706' : '#b45309',
        contrastText: mode === 'dark' ? '#000000' : '#ffffff',
      },
      secondary: {
        main: mode === 'dark' ? '#f48fb1' : '#dc004e',
        light: mode === 'dark' ? '#fce4ec' : '#ff5983',
        dark: mode === 'dark' ? '#c2185b' : '#9a0036',
      },
      background: {
        default: mode === 'dark' ? '#121212' : '#fafafa',
        paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Open Sans", "Noto Sans Tamil", sans-serif',
      fontSize: 16, // Increased base font size (default is 14)
      h1: { fontFamily: '"Montserrat", sans-serif', fontWeight: 800, fontSize: '3.5rem' },
      h2: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700, fontSize: '2.5rem' },
      h3: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700, fontSize: '2rem' },
      h4: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: '1.75rem' },
      h5: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: '1.5rem' },
      h6: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: '1.25rem' },
      subtitle1: { fontFamily: '"Open Sans", sans-serif', fontSize: '1.1rem' },
      subtitle2: { fontFamily: '"Open Sans", sans-serif', fontWeight: 600, fontSize: '1rem' },
      body1: { fontFamily: '"Open Sans", "Noto Sans Tamil", sans-serif', fontSize: '1.1rem', lineHeight: 1.6 },
      body2: { fontFamily: '"Open Sans", "Noto Sans Tamil", sans-serif', fontSize: '1rem', lineHeight: 1.6 },
      button: { fontFamily: '"Open Sans", sans-serif', fontWeight: 600, textTransform: 'none', fontSize: '1rem' },
    },
    components: {
      MuiContainer: {
        defaultProps: {
          maxWidth: 'xl', // Root Level Layout: Force wider container by default
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? '#f59e0b' : '#d97706', // Explicit Gold Border
            },
          }
        }
      }
    }
  })
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <AuthProvider>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Navigation mode={mode} onToggleTheme={toggleTheme} />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/poems" element={<ExplorePoems />} />
                <Route path="/poem/:id" element={<PoemDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/profile" element={<RequireUser>{withRouteSuspense(<UserProfile />)}</RequireUser>} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/oauth/callback" element={<OAuthCallback />} />
                <Route path="/oauth2/callback" element={<OAuthCallback />} />
                <Route path="/logout-confirmation" element={<LogoutConfirmation />} />
                {/* Admin routes: accessible to all authenticated users (user role) */}
                <Route path="/admin" element={<RequireUser>{withRouteSuspense(<Admin />)}</RequireUser>} />
                <Route path="/admin/poems" element={<RequireUser>{withRouteSuspense(<ManagePoems />)}</RequireUser>} />
                <Route path="/admin/poem/create" element={<RequireUser>{withRouteSuspense(<AdminPoemCreate />)}</RequireUser>} />
                <Route path="/admin/poem/edit/:id" element={<RequireUser>{withRouteSuspense(<AdminPoemEdit />)}</RequireUser>} />
                <Route path="/favorites" element={<RequireUser>{withRouteSuspense(<Favorites />)}</RequireUser>} />
                {/* Admin-only routes */}
                <Route path="/admin-panel" element={withRouteSuspense(<AdminPanel />)} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <Footer />
            </BrowserRouter>
            <ToastProvider />
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </HelmetProvider>
  )
}

export default App
